import { Modal, message } from 'antd';
import {
  normalizeInvitePermissions,
  type InvitePermissions,
} from '@todesktop/shared';
import { goToManageSecurityToken } from './uistate';
import { deleteAcceptedUser, updateInvitedUser } from '../firebase';
import { callFirebaseFunction } from '../firebase/functions';
import {
  authenticate,
  getSecurityTokenDetails,
  shouldBypassWebauthn,
} from '../firebase/webauthnUtils';
import { toError } from '../util/errors';

/**
 * Asks for a security key tap. Resolves to null, after explaining, when there
 * is no key or the tap fails.
 */
export const requestSecurityKey = async (action: string) => {
  const hasSecurityToken =
    shouldBypassWebauthn() || Boolean(await getSecurityTokenDetails());
  if (!hasSecurityToken) {
    Modal.confirm({
      title: `${action} requires a security token`,
      content: 'Would you like to add a security token now?',
      onOk: () => goToManageSecurityToken(),
    });
    return null;
  }
  try {
    return await authenticate();
  } catch (error) {
    message.error(
      toError(error, 'Failed to authenticate security token').message,
    );
    return null;
  }
};

// Teams the person is leaving themselves, so leaving isn't reported to them
// as being removed.
const leavingTeamIds = new Set<string>();

/** Whether a team disappeared because the person left it themselves. */
export const consumeLeftTeam = (accountId: string) =>
  leavingTeamIds.delete(accountId);

/** Resolves to whether they left. */
export const leaveTeam = async (accountId: string): Promise<boolean> => {
  const authentication = await requestSecurityKey('Leaving a team');
  if (!authentication) return false;
  leavingTeamIds.add(accountId);
  try {
    await deleteAcceptedUser(accountId, authentication);
    return true;
  } catch (error) {
    leavingTeamIds.delete(accountId);
    throw error;
  }
};

/** An Admin giving up Admin, keeping their other permissions. */
export const stepDownAsAdmin = async ({
  accountId,
  email,
  permissions,
}: {
  accountId: string;
  email: string;
  permissions?: InvitePermissions;
}): Promise<boolean> => {
  const authentication = await requestSecurityKey('Stepping down as Admin');
  if (!authentication) return false;
  await updateInvitedUser({
    accountId,
    email,
    permissions: { ...normalizeInvitePermissions(permissions), isAdmin: false },
    authentication,
  });
  return true;
};

const handOverTeamCallable = callFirebaseFunction('handOverTeam');
const deleteTeamCallable = callFirebaseFunction('deleteTeam');

/**
 * Makes another member an Admin, then keeps `keepPermissions` for the
 * viewer, or leaves when it's null. Resolves to whether it happened.
 */
export const handOverTeam = async ({
  accountId,
  toEmail,
  keepPermissions,
}: {
  accountId: string;
  toEmail: string;
  keepPermissions: InvitePermissions | null;
}): Promise<boolean> => {
  const authentication = await requestSecurityKey('Handing over a team');
  if (!authentication) return false;
  if (keepPermissions === null) leavingTeamIds.add(accountId);
  try {
    await handOverTeamCallable({
      accountId,
      toEmail,
      keepPermissions,
      authentication,
    });
    return true;
  } catch (error) {
    leavingTeamIds.delete(accountId);
    throw error;
  }
};

/** Deletes an empty shared team. Resolves to whether it happened. */
export const deleteTeam = async (accountId: string): Promise<boolean> => {
  const authentication = await requestSecurityKey('Deleting a team');
  if (!authentication) return false;
  leavingTeamIds.add(accountId);
  try {
    await deleteTeamCallable({ accountId, authentication });
    return true;
  } catch (error) {
    leavingTeamIds.delete(accountId);
    throw error;
  }
};
